<script lang="ts">
  import { cn } from '@/lib/utils';
  import Button from '@/lib/components/ui/button/button.svelte';
  import type { Exercise } from '@/Common/ActivityFunctions';
  import { Skeleton } from '@/lib/components/ui/skeleton';
  import TrainingCardSquare from './TrainingCardSquare.svelte';
  import { Card } from '@/lib/components/ui/card';
  import ExerciseDialog from './ExerciseDialog.svelte';
  import { getContext, tick } from 'svelte';
  import SearchFilter from './SearchFilter.svelte';
  import PageNavigator from '@/Common/PageNavigator.svelte';
  import { SearchFilterFieldsAndData } from './SearchFilterFieldsAndData';
  import * as Dialog from '$lib/components/ui/dialog';
  import TrainingCardLong from './TrainingCardLong.svelte';

  export let isLoading: boolean;
  export let originalExercises: Exercise[];
  export let onConfirm: ((exercise: Exercise) => void) | null = null;
  // if onClickCard is 'view details', clicking the card opens the details dialog
  // and the view details button at the bottom is not shown.
  export let onClickCard: ((exercise: Exercise) => void) | 'view details' = (exercise: Exercise) => { selectedExercise = exercise };
  export let actionButtonText: string = 'Select & Close';
  export let noExercisesFoundAdditionalInfo: string = '';
  export let selectedExercise: Exercise | null = null;
  export let showAddWorkoutButtons: boolean = false;

  let viewType: 'grid' | 'list' = 'list';

  let filteredExercises: Exercise[] = [];

  let searchFilterFieldsAndData: SearchFilterFieldsAndData;
  let filterDialogOpen = false; // for mobile view

  // Scroll content to top whenever filteredExercises changes
  let contentElement: HTMLElement | null = null;
  $: if (contentElement && filteredExercises) {
    contentElement.scrollTo({ top: 0, behavior: 'smooth' });
    resetCurrentPage();
  }

  const PAGE_SIZE = 24;
  let currentPage = 1;

  // IMPORTANT!!! this reactive statement has to come before the $: totalPages statement
  // otherwise, totalPages doesn't react to filteredExercises being set during the filter initialzation.
  // i don't know why.
  $: if (!isLoading && originalExercises) {
    initFilters();
  }
  $: totalPages = Math.ceil((filteredExercises?.length || 0) / PAGE_SIZE);

  // Separate function to avoid reactivity loop
  function resetCurrentPage() {
    currentPage = 1;
  }

  let moreDetailsDialogOpen = false;
  let selectedExerciseForDetailsDialog: Exercise | null = null;
  let lastDetailsButton: HTMLButtonElement | null = null; // button that opened details dialog

  let includeWorkoutTypeSelector = getContext<{[key: string]: any}>('workoutFilterParams')?.includeWorkoutTypeSelector ?? true;

  function initFilters() {
    searchFilterFieldsAndData = new SearchFilterFieldsAndData(
      originalExercises,
      (newFilteredExercises: Exercise[]) => {
        filteredExercises = newFilteredExercises;
      },
      () => { searchFilterFieldsAndData = searchFilterFieldsAndData; }
    );
  }

  // After dialog closes, explicitly return focus to the button that opened it, otherwise the content scrolls up to the top
  $: if (!moreDetailsDialogOpen) {
    tick().then(returnFocusToDetailsButton);
  }
  // This has to be a separate function so the reactive statement above doesn't react to changes to lastDetailsButton
  function returnFocusToDetailsButton() {
    if (!moreDetailsDialogOpen && lastDetailsButton) {
      try {
        // preventScroll not yet in all TS lib DOM typings -> cast
        (lastDetailsButton as any).focus({ preventScroll: true });
      } catch (e) {
        lastDetailsButton.focus();
      }
    }
  }

</script>

{#if !isLoading}
  <div class='hidden lg:block sticky top-[46px] w-[320px] max-h-[90dvh] h-full shrink-0 border-solid border-0 border-r border-border bg-muted/30 p-4 overflow-y-auto'>
    <SearchFilter
      bind:fields={searchFilterFieldsAndData}
      {includeWorkoutTypeSelector}
    />
  </div>
{/if}
<div bind:this={contentElement} class="h-full flex-1 flex flex-col gap-2 overflow-y-auto overflow-x-hidden py-4">
  {#if isLoading}
    <div class="flex flex-wrap gap-4 justify-center items-center">
      {#each Array(6) as _}
        <div class="w-72 h-72 flex flex-col gap-2">
          <Skeleton class="w-full flex-1" />
          <Skeleton class='w-[60%] h-5' />
          <div class='w-full flex flex-row justify-between'>
            <Skeleton class='w-[30%] h-5' />
            <Skeleton class='w-[30%] h-5' />
          </div>
        </div>
      {/each}
    </div>
  {:else}
    {#if showAddWorkoutButtons}
      <div class='flex flex-row gap-1 items-center justify-end w-full'>
        <Button size='sm' variant='outline' class='text-foreground no-underline' href="/workouts/import" target="workout">
          Import Workouts
        </Button>
        <Button size='sm' variant='outline' class='text-foreground no-underline' href="/workout" target="workout">
          Create Workout
        </Button>
      </div>
    {/if}
    <div class='lg:hidden flex flex-row gap-2 items-center'>
      <Button variant='outline' class='space-x-1 flex-1' on:click={() => filterDialogOpen = true}>
        <span>Filters</span>
        <i class='fa-light fa-sliders-up relative'>
          {#if searchFilterFieldsAndData.isFilterApplied()}
            <div class='absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500' />
          {/if}
        </i>
      </Button>
      <Button variant={viewType === 'list' ? 'outline' : 'ghost'} class={cn(viewType === 'list' ? 'text-black' : 'text-muted-foreground')} size='icon' on:click={() => viewType = 'list'}>
        <i class='fa-light fa-list-ul' aria-hidden='true' />
      </Button>
      <Button variant={viewType === 'grid' ? 'outline' : 'ghost'} class={cn(viewType === 'grid' ? 'text-black' : 'text-muted-foreground')} size='icon' on:click={() => viewType = 'grid'}>
        <i class='fa-light fa-grid-2' aria-hidden='true' />
      </Button>
    </div>
    <div class='relative flex flex-row items-center justify-center gap-2 lg:min-h-[36px]'>
      {#if totalPages > 1}
        <PageNavigator bind:currentPage {totalPages} />
      {/if}
      <div class='hidden lg:flex absolute top-0 bottom-0 right-2 flex-row gap-1'>
        <Button variant={viewType === 'list' ? 'outline' : 'ghost'} class={cn(viewType === 'list' ? 'text-black' : 'text-muted-foreground')} size='icon' on:click={() => viewType = 'list'}>
          <i class='fa-light fa-list-ul' aria-hidden='true' />
        </Button>
        <Button variant={viewType === 'grid' ? 'outline' : 'ghost'} class={cn(viewType === 'grid' ? 'text-black' : 'text-muted-foreground')} size='icon' on:click={() => viewType = 'grid'}>
          <i class='fa-light fa-grid-2' aria-hidden='true' />
        </Button>
      </div>
    </div>
    {#if viewType === 'grid'}
      <div class="flex flex-wrap gap-4 justify-center items-center">
        {#each filteredExercises?.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE) as exercise (exercise.path)}
          <Card
            class={cn(
              'border-border p-[8px] pt-[12px]',
              onClickCard ?
                'transition-all duration-150 ease-out transform-gpu will-change-transform hover:shadow-lg hover:-translate-y-0.5 hover:bg-muted' :
                'shadow-sm hover:shadow',
              exercise.path == selectedExercise?.path ? 'ring-1' : ''
            )}
            on:click={() => {
              if (onClickCard === 'view details') {
                selectedExerciseForDetailsDialog = exercise;
                moreDetailsDialogOpen = true;
              } else if (typeof onClickCard === 'function') {
                onClickCard?.(exercise);
              }
            }}
          >
            <div class="w-full space-y-1">
              <TrainingCardSquare size='lg' {exercise} />
              {#if onClickCard !== 'view details' || onConfirm}
                <div class='flex flex-row gap-2'>
                  {#if onClickCard !== 'view details'}
                    <Button size='sm' variant='outline' class='flex-1' on:click={(e) => {
                      e.stopPropagation();
                      lastDetailsButton = e.currentTarget;
                      selectedExerciseForDetailsDialog = exercise;
                      moreDetailsDialogOpen = true;
                    }}>
                      View Details
                    </Button>
                  {/if}
                  {#if onConfirm}
                    <Button size='sm' variant='outline' class='flex-1' on:click={(e) => {
                      e.stopPropagation();
                      onConfirm?.(exercise);
                    }}>
                      {actionButtonText}
                    </Button>
                  {/if}
                </div>
              {/if}
            </div>
          </Card>
        {/each}
      </div>
    {:else}
      <div class='flex flex-col gap-4 px-1'>
        {#each filteredExercises?.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE) as exercise (exercise.path)}
          <Card
            class={cn(
              'border-border p-[8px] pt-[12px]',
              onClickCard && typeof onClickCard === 'function' ?
                'transition-all duration-150 ease-out transform-gpu will-change-transform hover:shadow-lg hover:-translate-y-0.5 hover:bg-muted/30' :
                'shadow-sm hover:shadow',
              exercise.path == selectedExercise?.path ? 'ring-1' : ''
            )}
            on:click={() => {
              if (typeof onClickCard === 'function') {
                onClickCard?.(exercise);
              }
            }}
          >
            <TrainingCardLong {exercise}>
              {#if onConfirm}
                <Button size='sm' variant='default' on:click={(e) => {
                  e.stopPropagation();
                  onConfirm?.(exercise);
                }}>
                  {actionButtonText}
                </Button>
              {/if}
            </TrainingCardLong>
          </Card>
        {/each}
      </div> 
    {/if}
    {#if currentPage >= totalPages}
      <div class='flex flex-col gap-1 items-center my-8 text-center'>
        <div class="text-gray-600">Can't find what you're looking for? Check any filters you might have applied.</div>
        {#if noExercisesFoundAdditionalInfo}
          <div class="text-gray-500">{noExercisesFoundAdditionalInfo}</div>
        {/if}
      </div>
    {/if}
    {#if totalPages > 1}
      <div class='flex flex-row items-center justify-center gap-2'>
        <PageNavigator bind:currentPage {totalPages} />
      </div>
    {/if}
  {/if}
</div>

<ExerciseDialog bind:open={moreDetailsDialogOpen} exercise={selectedExerciseForDetailsDialog} />
<!-- Filters dialog for mobile view -->
<Dialog.Root bind:open={filterDialogOpen} portal="#shadcn-ui-dialog-target">
  <Dialog.Content class="flex flex-col max-h-[95dvh] md:h-[600px] max-w-[420px] w-[95vw] overflow-hidden rounded-xl px-3">
    <Dialog.Header class='px-3'>
      <Dialog.Title>
        Filters
      </Dialog.Title>
    </Dialog.Header>
    <div class='overflow-y-auto px-3'>
      <SearchFilter
        bind:fields={searchFilterFieldsAndData}
        {includeWorkoutTypeSelector}
        hideResetButton
      />
    </div>
    <Dialog.Footer class='gap-2 flex-row justify-end'>
      <Button variant='outline' on:click={() => {
        if (!searchFilterFieldsAndData) return;
        searchFilterFieldsAndData.resetFilters();
        // Force an assignment so Svelte propagates changes
        searchFilterFieldsAndData = searchFilterFieldsAndData;
      }}>
        Reset
      </Button>
      <Button variant='outline' on:click={() => filterDialogOpen = false}>
        Apply
      </Button>
    </Dialog.Footer>
  </Dialog.Content>
</Dialog.Root>
